/**
 * SalonDurumHaritasiPage.jsx — Salon durum haritası (backlog #183, 2026-09-26)
 * Route: /Management/Salonlar/:salonId/DurumHaritasi   (Salonlar satırı ve Salon Kartı'ndan açılır)
 *
 * Seçili fuarın bu salonundaki standları, seçilen DURUM BOYUTUNA göre krokide renklendirir:
 *   Genel      Standlar listesinin kenarlık kararı (StandPills.hasIssue)
 *   İnşa İzni  permitBadge — İş İzni  workPermitBadge — Borç  hasDebt
 * Kaynak: Standlar listesiyle aynı roster (GET /standTalep/list?fuarId&salonId, #68/#69).
 * FDS veriyi bilir, fdsvector çizer (#182) — renkler kaydedilmez, yalnızca görüntü.
 *
 * Renkler fdsvector'e URL parametresiyle gider (standColours=, view modu) — VITE_PARENT_ORIGIN
 * GEREKTİRMEZ, bugün çalışır. URL çok uzarsa (çok standlı salon) parametre bırakılır ve renkler
 * fdsvector:colour-stands mesajıyla gönderilir; o yol ve krokide stand tıklayınca Stand
 * Detay'a gitmek (#180) fdsvector VITE_PARENT_ORIGIN ile derlenene kadar ÇALIŞMAZ (backlog #184).
 * Bu yüzden sağdaki liste de her standa bağlantı verir — olaylar olmadan da tıklanabilir.
 *
 * Bir stand ancak fdsvector'de bir şekle BAĞLIYSA boyanır (şeklin data.standId'si = FDS stand id,
 * bkz. Stand Detay › Konum). Bağlı şekli olmayan standlar krokide boyanmaz; listede görünür.
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Container, Row, Col, Card, CardHeader, CardBody, Button, ButtonGroup, Badge, Spinner, Alert, Table,
} from "reactstrap";
import { ArrowLeft, ExternalLink } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import KrokiVectorEmbed from "../../../Components/Kroki/KrokiVectorEmbed";
import {
  permitBadge, workPermitBadge, hasIssue, STATUS_HEX, NEUTRAL_GRAY,
} from "../../../Components/Projects/StandPills";
import { getTumStandlarList } from "../../../services/standlarListService";
import { resolveStandPlanDocument, VECTOR_COMMANDS } from "../../../services/krokiVectorService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const PAGE_SIZE = 500;
const MAX_STANDS = 3000;
// fdsvector'ün SPA'sı standart bir sunucudan gelir; istek satırı ~8 KB'ı geçmesin.
const MAX_URL_PARAM_LENGTH = 6000;

// Rozet rengi → krokide dolgu. secondary / bilinmeyen = nötr gri (StandPills.statusBackground ile aynı).
const hexOf = (color) => STATUS_HEX[color] ?? NEUTRAL_GRAY;

const BOYUTLAR = [
  {
    key: "genel", get label() { return i18n.t("yonetim:salonDurumHaritasi.genel"); },
    of: (s) => (!s.firmaId
      ? { label: i18n.t("yonetim:salonDurumHaritasi.katilimciYok"), color: "secondary" }
      : hasIssue(s) ? { label: i18n.t("yonetim:salonDurumHaritasi.dikkatGerekiyor"), color: "danger" } : { label: i18n.t("yonetim:salonDurumHaritasi.sorunYok"), color: "success" }),
  },
  { key: "insaIzni", get label() { return i18n.t("yonetim:salonDurumHaritasi.insaIzni"); }, of: (s) => permitBadge(s) },
  { key: "isIzni", get label() { return i18n.t("yonetim:salonDurumHaritasi.isIzni"); }, of: (s) => workPermitBadge(s) },
  {
    key: "borc", get label() { return i18n.t("yonetim:salonDurumHaritasi.borc"); },
    of: (s) => (s.hasDebt ? { label: i18n.t("yonetim:salonDurumHaritasi.borcVar"), color: "danger" } : { label: i18n.t("yonetim:salonDurumHaritasi.borcYok"), color: "success" }),
  },
];

const SIRA = { danger: 0, warning: 1, info: 2, success: 4 };

export default function SalonDurumHaritasiPage() {
  const { t } = useTranslation("yonetim");
  const { salonId } = useParams();
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);

  const [stands, setStands] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [probe, setProbe] = useState(null);
  const [boyutKey, setBoyutKey] = useState("genel");

  useEffect(() => { document.title = i18n.t("yonetim:salonDurumHaritasi.salonDurumHaritasiFds"); }, []);

  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        // Kroki yoklaması ve roster paralel; roster sayfalı, salon başına birkaç yüz stand olur.
        const probePromise = resolveStandPlanDocument({ fairId: selectedFuarId, hallId: salonId });
        const all = [];
        let page = 1;
        let total = Infinity;
        while (all.length < total && all.length < MAX_STANDS) {
          // eslint-disable-next-line no-await-in-loop
          const res = await getTumStandlarList({ fuarId: selectedFuarId, salonId, page, limit: PAGE_SIZE });
          if (ignore) return;
          total = res.total ?? 0;
          all.push(...(res.records ?? []));
          if ((res.records ?? []).length < PAGE_SIZE) break;
          page += 1;
        }
        const p = await probePromise;
        if (ignore) return;
        setStands(all);
        setTruncated(all.length < total);
        setProbe(p);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [selectedFuarId, salonId]);

  const boyut = BOYUTLAR.find((b) => b.key === boyutKey) ?? BOYUTLAR[0];

  const rows = useMemo(() => stands.map((s) => ({ s, badge: boyut.of(s) }))
    .sort((a, b) => (SIRA[a.badge.color] ?? 3) - (SIRA[b.badge.color] ?? 3)
      || String(a.s.standName ?? "").localeCompare(String(b.s.standName ?? ""), "tr")),
  [stands, boyut]);

  const legend = useMemo(() => {
    const byLabel = new Map();
    rows.forEach(({ badge }) => {
      const e = byLabel.get(badge.label) ?? { label: badge.label, color: badge.color, count: 0 };
      e.count += 1;
      byLabel.set(badge.label, e);
    });
    return [...byLabel.values()].sort((a, b) => (SIRA[a.color] ?? 3) - (SIRA[b.color] ?? 3));
  }, [rows]);

  const colours = useMemo(
    () => Object.fromEntries(rows.map(({ s, badge }) => [String(s.standId), hexOf(badge.color)])),
    [rows],
  );
  // URLSearchParams ':' '#' ',' karakterlerini kodlar (~3 kat) — uzunluğu kodlanmış hâliyle ölç.
  const urlLength = useMemo(
    () => encodeURIComponent(Object.entries(colours).map(([id, c]) => `${id}:${c}`).join(",")).length,
    [colours],
  );
  const urlUygun = urlLength <= MAX_URL_PARAM_LENGTH;

  const viewOptions = useMemo(() => ({
    layers: "none",
    forceLabels: true,
    ...(urlUygun ? { standColours: colours } : {}),
  }), [urlUygun, colours]);

  const commandOnLoad = useMemo(() => (urlUygun ? undefined : {
    type: VECTOR_COMMANDS.COLOUR_STANDS,
    colours,
    legend: legend.map((l) => ({ label: l.label, colour: hexOf(l.color) })),
  }), [urlUygun, colours, legend]);

  const salonName = stands[0]?.salonName ?? null;

  if (!selectedFuarId) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("salonDurumHaritasi.durumHaritasi")} pageTitle={t("salonDurumHaritasi.salonlarVeKrokiler")} />
          <Alert color="warning">{t("salonDurumHaritasi.onceUsttenBirFuarSecin")}</Alert>
        </Container>
      </div>
    );
  }

  let kroki;
  if (loading || !probe) {
    kroki = <div className="text-center py-5"><Spinner color="primary" /></div>;
  } else if (probe.reason === "not-mapped" || probe.reason === "missing-id") {
    kroki = (
      <Alert color="warning" className="mb-0">
        {t("salonDurumHaritasi.buSalonunBuFuarIcin")}
      </Alert>
    );
  } else {
    kroki = (
      <>
        {!probe.ok && (
          <Alert color="warning" className="py-2 small">
            {t("salonDurumHaritasi.krokiOncedenDogrulanamadiGomuluUygulama", { message: probe.message })}
          </Alert>
        )}
        {!urlUygun && (
          <Alert color="warning" className="py-2 small">
            {t("salonDurumHaritasi.buSalondaCokStandVar", { length: rows.length })}
          </Alert>
        )}
        <KrokiVectorEmbed
          fairId={selectedFuarId}
          hallId={salonId}
          hallName={salonName ?? undefined}
          allowEdit={false}
          height={640}
          viewOptions={viewOptions}
          commandOnLoad={commandOnLoad}
          onStandClick={(e) => { if (e?.standId) navigate(`/Management/Standlar/${e.standId}`); }}
        />
      </>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("salonDurumHaritasi.durumHaritasi")} pageTitle={t("salonDurumHaritasi.salonlarVeKrokiler")} />
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

        <Card className="mb-3">
          <CardBody className="py-2 d-flex align-items-center gap-3 flex-wrap">
            <Link to={`/Management/Salonlar/${salonId}`} className="btn btn-light btn-sm">
              <ArrowLeft size={14} className="me-1" />{t("salonDurumHaritasi.salonKarti")}
            </Link>
            <h5 className="mb-0 flex-grow-1">
              {t("salonDurumHaritasi.salon")}{" "}{salonName ?? `#${salonId}`}{" "}{t("salonDurumHaritasi.durumHaritasi2")}
              <Badge color="light" className="text-dark ms-2 align-middle">{stands.length}{" "}{t("salonDurumHaritasi.stand")}</Badge>
            </h5>
            <ButtonGroup size="sm">
              {BOYUTLAR.map((b) => (
                <Button key={b.key} color="primary" outline={boyutKey !== b.key} onClick={() => setBoyutKey(b.key)}>
                  {b.label}
                </Button>
              ))}
            </ButtonGroup>
          </CardBody>
        </Card>

        {truncated && (
          <Alert color="warning" className="py-2 small">{t("salonDurumHaritasi.ilkStandGosteriliyor", { length: stands.length })}</Alert>
        )}

        <Row className="g-3">
          <Col xl={8}>
            <Card>
              <CardHeader className="d-flex align-items-center gap-2 flex-wrap py-2">
                <span className="fw-semibold me-2">{boyut.label}</span>
                {legend.map((l) => (
                  <span key={l.label} className="d-inline-flex align-items-center gap-1 small">
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: hexOf(l.color), display: "inline-block" }} />
                    {l.label} <span className="text-muted">({l.count})</span>
                  </span>
                ))}
              </CardHeader>
              <CardBody>{kroki}</CardBody>
            </Card>
          </Col>
          <Col xl={4}>
            <Card>
              <CardHeader className="py-2"><span className="fw-semibold">{t("salonDurumHaritasi.standlar")}</span></CardHeader>
              <CardBody className="p-0" style={{ maxHeight: 720, overflowY: "auto" }}>
                {loading ? (
                  <div className="text-center py-4"><Spinner color="primary" /></div>
                ) : rows.length === 0 ? (
                  <div className="text-muted text-center py-4">{t("salonDurumHaritasi.buSalondaBuFuarIcin")}</div>
                ) : (
                  <Table size="sm" className="align-middle mb-0">
                    <tbody>
                      {rows.map(({ s, badge }) => (
                        <tr key={s.standId}>
                          <td className="ps-3">
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: hexOf(badge.color), display: "inline-block" }} className="me-2" />
                            <span className="fw-semibold">{s.standName}</span>
                            <div className="text-muted" style={{ fontSize: 11 }}>{s.firmaShortName || s.firmaName || "—"}</div>
                          </td>
                          <td className="small">{badge.label}</td>
                          <td className="text-end pe-3">
                            <Link to={`/Management/Standlar/${s.standId}`} title={t("salonDurumHaritasi.standDetay")} className="btn btn-sm btn-light">
                              <ExternalLink size={12} />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
